// primary-care-connect-main/frontend/src/pages/Portal/ClinicAdmin/FamilyDoctors/components/FamilyDoctorRow.tsx

import React from "react";

export type FamilyDoctorStatus = "Active" | "Invited" | "Inactive";

export type FamilyDoctorRowData = {
  id: number;
  displayName: string;
  cpscNumber?: string;
  email?: string;
  gender?: string;
  status: FamilyDoctorStatus;
  invitedAt?: string;
};

type Props = {
  familyDoctor: FamilyDoctorRowData;

  /**
   * Open details side panel.
   * Only applicable for Active/Inactive rows (real FamilyDoctorID).
   */
  onOpenDetails?: () => void;

  // Action callbacks (page decides behaviour)
  onDeactivate?: (familyDoctorId: number) => void;
  onReactivate?: (familyDoctorId: number) => void;

  /**
   * NOTE:
   * For Invited rows, `familyDoctor.id` is ProviderInviteID (not FamilyDoctorID).
   */
  onResendInvite?: (providerInviteId: number) => void;
  onRevokeInvite?: (providerInviteId: number) => void;
};

export default function FamilyDoctorRow({
  familyDoctor,
  onOpenDetails,
  onDeactivate,
  onReactivate,
  onResendInvite,
  onRevokeInvite,
}: Props) {
  const { id, displayName, cpscNumber, email, gender, status, invitedAt } =
    familyDoctor;

  const invitedAtLabel =
    status === "Invited" && invitedAt ? ` • ${invitedAt}` : null;

  const nameClickable = status !== "Invited" && !!onOpenDetails;

  // Keep v1 table compact: show gender only when we have it (and especially useful for Invited rows)
  const genderLabel =
    gender && String(gender).trim().length ? String(gender).trim() : null;

  return (
    <tr>
      <td className="provider-name">
        {nameClickable ? (
          <button
            type="button"
            className="provider-name-link"
            onClick={onOpenDetails}
            aria-label={`Open details for ${displayName}`}
          >
            {displayName}
          </button>
        ) : (
          <div>
            <div>{displayName}</div>
            {genderLabel ? (
              <div className="muted" style={{ marginTop: 2 }}>
                {genderLabel}
              </div>
            ) : null}
          </div>
        )}
      </td>

      <td>{cpscNumber || "—"}</td>
      <td>{email || "—"}</td>

      <td>
        <span className={`status-pill status-${status.toLowerCase()}`}>
          {status}
          {invitedAtLabel}
        </span>
      </td>

      <td className="actions-col">
        {status === "Active" && (
          <button
            className="link-btn"
            onClick={() => onDeactivate && onDeactivate(id)}
          >
            Deactivate
          </button>
        )}

        {status === "Inactive" && (
          <button
            className="link-btn"
            onClick={() => onReactivate && onReactivate(id)}
          >
            Reactivate
          </button>
        )}

        {status === "Invited" && (
          <div className="row-actions">
            <button
              className="link-btn"
              onClick={() => onResendInvite && onResendInvite(id)}
            >
              Resend Invitation
            </button>

            <button
              className="link-btn danger"
              onClick={() => onRevokeInvite && onRevokeInvite(id)}
            >
              Revoke
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
